// Blocks whose orientation the layout cannot infer.
import { StudioDraft, StudioItem, StudioRunGeometry, effectiveAttrs } from "../studioTypes";
import { BlockOrientation, buildFromCdt, poolingKeyOf } from "./buildFromCdt";

/** The spec's ambiguity band, in facings per shelf (§9). */
export const AMBIGUITY_BAND: readonly [number, number] = [2.0, 2.5];

export interface PoolingStatus {
  attr: string;
  value: string;
  /** Total facings over the run's shelf count. */
  facingsPerShelf: number;
  built: BlockOrientation;
  answer?: BlockOrientation;
  /** Inside the ambiguity band — the cases to ask about. */
  ambiguous: boolean;
}

export const poolingQuestionOf = (
  items: readonly StudioItem[],
  runs: readonly StudioRunGeometry[],
  draft: StudioDraft,
): PoolingStatus[] => {
  if (items.length === 0 || runs.length === 0 || draft.spine.length === 0) return [];
  const roleProbe = buildFromCdt(items, runs, draft, {
    poolingOverrides: draft.poolingOverrides,
  });
  const vLevels = roleProbe.report.roles
    .filter((role) => role.role === "vertical")
    .map((role) => role.level);
  const shelvesOf = new Map(runs.map((run) => [run.run, run.shelves.length]));

  const out: PoolingStatus[] = [];
  for (const attr of vLevels) {
    // Must build under the same options applyHierarchySwap uses, identityAttr included.
    const { report } = buildFromCdt(items, runs, draft, {
      poolingOverrides: draft.poolingOverrides,
      identityAttr: attr,
    });
    const windowOf = new Map<string, { pooled: boolean }>();
    for (const win of report.windows) {
      for (const skuId of win.skuIds) windowOf.set(skuId, win);
    }
    const byValue = new Map<string, StudioItem[]>();
    for (const item of items) {
      const value = String(effectiveAttrs(item, draft)[attr] ?? "");
      if (!value || value === "?") continue;
      const list = byValue.get(value) ?? [];
      list.push(item);
      byValue.set(value, list);
    }
    for (const [value, own] of byValue) {
      const facings = own.reduce((sum, item) => sum + item.facings, 0);
      const runId = own[0]?.run ?? runs[0].run;
      const ratio = facings / Math.max(1, shelvesOf.get(runId) ?? 1);
      const built: BlockOrientation = own.some((item) => windowOf.get(item.skuId)?.pooled)
        ? "pooled"
        : "vertical";
      out.push({
        attr,
        value,
        facingsPerShelf: +ratio.toFixed(2),
        built,
        answer: draft.poolingOverrides?.[poolingKeyOf(attr, value)],
        ambiguous: ratio >= AMBIGUITY_BAND[0] && ratio < AMBIGUITY_BAND[1],
      });
    }
  }
  return out;
};
